import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Typography, Container,
} from '@mui/material';
import { Home, BugReport, ArrowBack } from '@mui/icons-material';
import './NotFound.scss';

function NotFound() {
  const navigate = useNavigate();

  return (
    <Box
      className="not-found-container"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(360deg, #61b9c3 0%, rgba(67, 236, 208, 1) 100%)',
        padding: 2,
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            textAlign: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '20px',
            padding: { xs: 4, md: 6 },
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* 404 Animation */}
          <Box
            className="error-animation"
            sx={{
              fontSize: { xs: '6rem', md: '8rem' },
              fontWeight: 'bold',
              color: '#61b9c3',
              marginBottom: 2,
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)',
              animation: 'bounce 2s infinite',
              '@keyframes bounce': {
                '0%, 20%, 50%, 80%, 100%': {
                  transform: 'translateY(0)',
                },
                '40%': {
                  transform: 'translateY(-10px)',
                },
                '60%': {
                  transform: 'translateY(-5px)',
                },
              },
            }}
          >
            404
          </Box>

          {/* Floating Elements */}
          <Box
            sx={{
              position: 'relative',
              marginBottom: 3,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#333',
                marginBottom: 2,
                fontSize: { xs: '1.5rem', md: '2rem' },
              }}
            >
              Oops! Page Not Found
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#666',
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.6,
                maxWidth: '600px',
                margin: '0 auto',
                marginBottom: 4,
              }}
            >
              The page you're looking for seems to have wandered off into the digital wilderness.
              Don't worry though - we'll help you find your way back to familiar territory.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<Home />}
              onClick={() => navigate('/dashboard')}
              sx={{
                backgroundColor: '#61b9c3',
                color: 'white',
                borderRadius: '12px',
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 15px rgba(97, 185, 195, 0.4)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#43ecd0',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(67, 236, 208, 0.5)',
                },
              }}
            >
              Back to Dashboard
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              sx={{
                borderColor: '#61b9c3',
                color: '#61b9c3',
                borderRadius: '12px',
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderWidth: '2px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#43ecd0',
                  backgroundColor: 'rgba(97, 185, 195, 0.05)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Go Back
            </Button>

            <Button
              variant="text"
              size="large"
              startIcon={<BugReport />}
              onClick={() => window.open('https://evalue8.net/support/', '_blank')}
              sx={{
                color: '#43ecd0',
                borderRadius: '12px',
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(67, 236, 208, 0.1)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Report Issue
            </Button>
          </Box>

          {/* Decorative Elements */}
          <Box
            sx={{
              marginTop: 4,
              opacity: 0.6,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#999',
                fontSize: '0.9rem',
              }}
            >
              Error Code: PAGE_NOT_FOUND
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default NotFound;
